<script lang="ts" setup>
import MainLayout from '@/common/components/MainLayout.vue';
import { Card, Button, DataTable, Column, Menu, InputText, type DataTableRowClickEvent, type DataTableRowDoubleClickEvent, useToast } from 'primevue';
import type { MenuItem } from 'primevue/menuitem';
import { computed, ref, useTemplateRef } from 'vue';
import { clearCorruptDataTableState } from 'shared/common/dataTableState';
import { useDataTableMultiSort } from 'shared/common/composables/useDataTableMultiSort';
import { useColumnSelector, type ColumnField } from 'shared/common/composables/useColumnSelector';
import { useColumnFilters } from 'shared/common/composables/useColumnFilters';
import ColumnSelectorButton from 'shared/common/components/ColumnSelectorButton.vue';
import ColumnFilterCell from 'shared/common/components/ColumnFilterCell.vue';
import DeleteMapStyleDialog from './components/DeleteMapStyleDialog.vue';
import EditMapStyleDialog from './components/EditMapStyleDialog.vue';
import { useMapStyleStore } from 'shared/mapStyles/stores/mapStyleStore';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { storeToRefs } from 'pinia';
import { useGetMapStyle } from './api/getMapStyle';
import { useActionFailedAlert } from 'shared/problems/composables/actionFailedAlert';
import type { MapStyle } from './api/createMapStyle';

const mapStyleStore = useMapStyleStore();
const getMapStyle = useGetMapStyle();
const actionFailedAlert = useActionFailedAlert();
const toast = useToast();

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);
const pageTranslations = computed(() => translations.value.mapStyles.page);

const styles = computed(() => {
  const result = mapStyleStore.styles.map<StyleItem>(s => {
    return {
      id: s.id,
      name: s.name,
      nameSortOrder: 0
    };
  });

  result.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));

  for (let i = 0; i < result.length; i++) {
    const item = result[i];
    if (item) {
      item.nameSortOrder = i;
    }
  }

  return result;
});
const selectedStyles = ref<StyleItem[]>([]);
const loading = computed(() => mapStyleStore.loading);
const menuStyleId = ref<string>();
const { multiSortMeta, sortBadgeClass } = useDataTableMultiSort();

const columnDefinitions = computed<ColumnField[]>(() => [
  { field: 'name', header: translations.value.generic.name, filterType: 'text' },
]);
const { orderedColumns, selectedColumns, isVisible, setSelectedColumns, setOrder, reset: resetColumns } =
  useColumnSelector({ storageKey: 'map-style-table-columns', columns: columnDefinitions });

const { filters, filteredCount, hasActiveFilters, clearFilters, onFilter } = useColumnFilters({
  columns: columnDefinitions,
  totalCount: () => styles.value.length,
});

const menu = useTemplateRef('style-menu');
const editDialog = useTemplateRef('edit-dialog');
const deleteDialog = useTemplateRef('delete-dialog');

const menuItems = computed<MenuItem[]>(() => [
  {
    label: translations.value.generic.edit,
    icon: 'pi pi-pencil',
    command() {
      const id = menuStyleId.value;
      if (!id) return;
      showStyle(id);
    },
  },
  {
    label: translations.value.generic.delete,
    icon: 'pi pi-trash',
    command() {
      const id = menuStyleId.value;
      if (!id) return;
      deleteDialog.value?.show(id);
    }
  },
  {
    label: translations.value.generic.export,
    icon: 'pi pi-file-export',
    command() {
      const id = menuStyleId.value;
      if (!id) return;
      exportToClipboard(id);
    }
  }
]);

// Clear corrupt persisted sort state before the DataTable restores it while rendering.
clearCorruptDataTableState('map-style-table-state');

function onAddNewClick() {
  editDialog.value?.show({});
}

function onStyleClick(ev: DataTableRowClickEvent) {
  const target = ev.originalEvent.target as HTMLElement | null;
  if (!target) return;
  const name = target.closest('.style-name');
  if (!name) return;
  showStyle(ev.data.id);
}

function onStyleDoubleClick(ev: DataTableRowDoubleClickEvent) {
  const style = ev.data as StyleItem;
  showStyle(style.id);
}

async function showStyle(id: string) {

  const response = await getMapStyle.execute({ styleId: id });

  if (!response.ok) {
    actionFailedAlert.show(response.details);
    return;
  }

  const style = response.style;
  editDialog.value?.show(style);
}

function toggleStyleMenu(ev: Event, styleId: string) {
  menuStyleId.value = styleId;
  menu.value?.toggle(ev);
}

async function exportToClipboard(id: string) {

  const response = await getMapStyle.execute({ styleId: id });

  if (!response.ok) {
    actionFailedAlert.show(response.details);
    return;
  }

  const style = response.style;

  const request: MapStyle = {
    name: style.name,

    shapeColorFormula: style.shapeColorFormula ?? null,
    shapeLabelFormula: style.shapeLabelFormula ?? null,
    shapeOutlineColorFormula: style.shapeOutlineColorFormula ?? null,
    shapeOutLineSizeFormula: style.shapeOutlineSizeFormula ?? null,
    shapeFontSizeFormula: style.shapeFontSizeFormula ?? null,
    shapeMessageIconFormula: style.shapeMessageIconFormula ?? null,

    vehicleColorFormula: style.vehicleColorFormula ?? null,

    placeVisibleFormula: style.placeVisibleFormula ?? null,
    placeLabelFormula: style.placeLabelFormula ?? null,
    placeOutlineColorFormula: style.placeOutlineColorFormula ?? null,
    placeOutlineSizeFormula: style.placeOutlineSizeFormula ?? null,
    placeAccessibleOutlineColorFormula: style.placeAccessibleOutlineColorFormula ?? null,
    placeInaccessibleOutlineColorFormula: style.placeInaccessibleOutlineColorFormula ?? null,
    placeTextAlignmentFormula: style.placeTextAlignmentFormula ?? null,
    placeTextPositionFormula: style.placeTextPositionFormula ?? null,
    placeTextOpacityFormula: style.placeTextOpacityFormula ?? null,
    placeTextColorFormula: style.placeTextColorFormula ?? null,
    placeTextSizeFormula: style.placeTextSizeFormula ?? null,
    placeBackgroundColorFormula: style.placeBackgroundColorFormula ?? null,
    placeBackgroundPatternFormula: style.placeBackgroundPatternFormula ?? null,
    placeBackgroundOpacityFormula: style.placeBackgroundOpacityFormula ?? null,

    packageVisibleFormula: style.packageVisibleFormula ?? null,
    packageLabelFormula: style.packageLabelFormula ?? null,
    packageOutlineColorFormula: style.packageOutlineColorFormula ?? null,
    packageOutlineSizeFormula: style.packageOutlineSizeFormula ?? null,
    packageTextAlignmentFormula: style.packageTextAlignmentFormula ?? null,
    packageTextPositionFormula: style.packageTextPositionFormula ?? null,
    packageTextOpacityFormula: style.packageTextOpacityFormula ?? null,
    packageTextColorFormula: style.packageTextColorFormula ?? null,
    packageTextSizeFormula: style.packageTextSizeFormula ?? null,
    packageBackgroundColorFormula: style.packageBackgroundColorFormula ?? null,
    packageBackgroundPatternFormula: style.packageBackgroundPatternFormula ?? null,
    packageBackgroundOpacityFormula: style.packageBackgroundOpacityFormula ?? null,
    proximityVisibleFormula: style.proximityVisibleFormula ?? null,
    proximityAreaColorFormula: style.proximityAreaColorFormula ?? null,
    proximityConeColorFormula: style.proximityConeColorFormula ?? null
  };

  const json = JSON.stringify(request);
  await navigator.clipboard.writeText(json);

  toast.add({
    summary: translations.value.mapStyles.page.copiedToClipboard,
    severity: 'success',
    life: 3000
  });
}

async function importFromClipboard() {
  try {
    const json = await navigator.clipboard.readText();

    if (!json) return;

    const style = JSON.parse(json) as MapStyle;
    editDialog.value?.show(style);

  } catch (error) {
    console.error('Failed to import from clipboard:', error);

    if (error instanceof DOMException && error.name === 'NotAllowedError') {
      toast.add({
        summary: translations.value.mapStyles.page.clipBoardAccessDeniedSummary,
        life: 5000,
        severity: 'error',
        detail: translations.value.mapStyles.page.clipBoardAccessDeniedDetail
      });

      return;
    }

    toast.add({
      summary: translations.value.mapStyles.page.noStyleInClipboard,
      life: 5000,
      severity: 'error',
      detail: translations.value.mapStyles.page.noStyleInClipboardDescription
    });
  }
}


interface StyleItem {
  id: string;
  name: string;
  nameSortOrder: number;
}

</script>

<template>
  <MainLayout>
    <Card class="style-card">
      <template #title>
        <span>{{ pageTranslations.title }}</span>
        <div class="filter-info">
          <span v-if="hasActiveFilters && filteredCount < styles.length" class="filtered-count">
            ({{ translations.dataTable.showing }} {{ filteredCount }} {{ translations.dataTable.of }} {{ styles.length }})
          </span>
          <Button v-if="hasActiveFilters" type="button" severity="secondary" size="small" icon="pi pi-filter-slash"
            :label="translations.dataTable.clearFilters" @click="clearFilters" />
        </div>
        <Button class="add-button" :label="translations.generic.add" @click="onAddNewClick">
        </Button>
        <Button class="import-button" v-tooltip.bottom="translations.mapStyles.page.importFromClipboardTooltip"
          :label="translations.generic.import" @click="importFromClipboard">
        </Button>
        <InputText class="search-field" autofocus v-model="filters.global.value"
          :placeholder="translations.generic.search" />
      </template>
      <template #content>
        <DataTable class="style-table" v-model:selection="selectedStyles" sort-mode="multiple" v-model:multiSortMeta="multiSortMeta" :class="sortBadgeClass" v-model:filters="filters"
          :value="styles" data-key="id" scrollable scrollHeight="flex" meta-key-selection
          @row-dblclick="onStyleDoubleClick" :loading="loading" @row-click="onStyleClick" stateStorage="local" stateKey="map-style-table-state" filterDisplay="menu" @filter="onFilter">
          <template #empty>{{ pageTranslations.notFound }}</template>
          <template #loading>{{ pageTranslations.loading }}</template>
          <Column v-if="isVisible('name')" field="name" class="style-name" :header="translations.generic.name" sortable
            sort-field="nameSortOrder" filter filterField="name">
            <template #filter="{ filterModel, filterCallback }">
              <ColumnFilterCell filter-type="text" :filter-model="filterModel" :filter-callback="filterCallback"
                :placeholder="translations.dataTable.filterBy + ' ' + translations.generic.name.toLocaleLowerCase()" />
            </template>
          </Column>
          <Column class="w-24 text-end!">
            <template #header>
              <ColumnSelectorButton :columns="orderedColumns" :modelValue="selectedColumns"
                @update:modelValue="setSelectedColumns" @reorder="setOrder" @reset="resetColumns" />
            </template>
            <template #body="style">
              <Button icon="pi pi-ellipsis-v" variant="text" severity="secondary" rounded
                @click="(ev) => toggleStyleMenu(ev, style.data.id)"></Button>
            </template>
          </Column>
        </DataTable>
      </template>
    </Card>
  </MainLayout>

  <EditMapStyleDialog ref="edit-dialog"></EditMapStyleDialog>
  <DeleteMapStyleDialog ref="delete-dialog"></DeleteMapStyleDialog>
  <Menu ref="style-menu" :model="menuItems" :popup="true" />

</template>

<style scoped>
:deep(.p-datatable-hoverable .p-datatable-selectable-row) {
  cursor: unset;
}

:deep(.p-card-title) {
  display: grid;
  grid-template-columns: auto 1fr auto auto 1rem auto;
  column-gap: 0.5rem;
  align-items: center;
}

.filter-info {
  grid-column: 2;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
}

.filtered-count {
  font-weight: 400;
  font-size: 0.875rem;
  color: var(--p-text-muted-color);
}

.add-button {
  grid-column: 3;
}

.import-button {
  grid-column: 4;
}

.search-field {
  grid-column: 6;
}

:deep(.style-name) {
  cursor: pointer;
}

:deep(.style-name:hover) {
  color: var(--p-primary-900);
  background-color: var;
  text-decoration: underline;
}

.style-card {
  overflow-y: hidden;
  height: 100%;
}

:deep(.p-card-body) {
  overflow-y: hidden;
  height: 100%;
}

:deep(.p-card-content) {
  overflow-y: hidden;
  height: 100%;
}
</style>
